import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Phone, MessageCircle, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { BRAND, waLink } from "@/data/site";

const SEEN_KEY = "nbwa_popup_seen";

const EnquiryPopup = () => {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/booking") return;
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setShow(true), 22000);
    return () => clearTimeout(t);
  }, [pathname]);

  const dismiss = () => {
    sessionStorage.setItem(SEEN_KEY, "1");
    setShow(false);
  };

  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
          role="dialog"
          aria-modal="true"
          aria-label="Enquire about classes"
          data-testid="enquiry-popup"
        >
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md border border-white/12 bg-[#0B0B10] p-8 shadow-[0_0_60px_rgba(220,38,38,0.15)] sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-700" aria-hidden="true" />
            <button
              onClick={dismiss}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-red-500 hover:text-red-400"
              aria-label="Close"
              data-testid="popup-close"
            >
              <X size={16} />
            </button>
            <Logo compact />
            <h2 className="mt-6 font-display text-2xl font-black uppercase leading-tight tracking-tight text-white">
              Ready to begin <span className="text-red-500">your journey?</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              30+ years of training, creativity &amp; performance. Talk to us today — we'll help you find the
              right class.
            </p>
            <div className="mt-7 space-y-2.5">
              <a
                href={BRAND.phoneLink}
                onClick={dismiss}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-rose-700 px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-white"
                data-testid="popup-call"
              >
                <Phone size={15} /> Call Now
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismiss}
                className="flex items-center justify-center gap-2 border border-emerald-500/50 px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-400 transition-colors hover:bg-emerald-500/10"
                data-testid="popup-whatsapp"
              >
                <MessageCircle size={15} /> WhatsApp Us
              </a>
              <a
                href="/booking?mode=enquiry"
                onClick={dismiss}
                className="flex items-center justify-center gap-2 border border-white/15 px-6 py-3.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-white transition-colors hover:border-red-500 hover:text-red-400"
                data-testid="popup-enquire"
              >
                Enquire Now <ArrowRight size={15} />
              </a>
            </div>
            <button
              onClick={dismiss}
              className="mt-5 w-full text-center font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:text-zinc-300"
              data-testid="popup-later"
            >
              Maybe later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryPopup;
